import { useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  GqlFollowingsDocument,
  NewsItemHeader_NewsItemFragment,
  useCurrentUserQuery,
  useFollowChannelMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import UnFollowModal from '@app.feature/channel/component/modals/UnFollowModal'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import { convertCreatedAtToString } from 'app.modules/util/time'
import { mobileBreakPoint } from 'app.styled/media'
import LoginAlert from 'components/LoginAlert'
import Avatar from '../../../../../app.components/Avatar/Avatar'
import DropdownBtn from './DropdownBtn'

gql`
  fragment NewsItemHeader_NewsItem on NewsItem {
    id
    viewerIsAuthor
    viewerHasReported
    viewerCanDelete
    createdAt
    updatedAt
    deletedAt
    isNotice
    channel {
      id
      url
      name
      viewerIsOwner
      viewerHasFollowed
      owner {
        id
        logoImage {
          id
          url
        }
      }
    }
  }
`

type TProps = {
  data: NewsItemHeader_NewsItemFragment
  type?: 'feed' | 'modal' | 'card'
  className?: string
  showFollowBtn?: boolean
}

const NewsItemHeader: React.FC<TProps> = ({
  type = 'feed',
  data,
  className,
  showFollowBtn,
}) => {
  const router = useRouter()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const onError = useChannelError()
  const currentUserQuery = useCurrentUserQuery()
  const followChannelMutation = useFollowChannelMutation({
    onError,
  })
  const [loginModalOpened, setLoginModalOpened] = useState(false)
  const [unFollowModalOpened, setUnFollowModalOpened] = useState(false)

  const handleFollowBtn = () => {
    if (!currentUserQuery.data?.currentUser) return setLoginModalOpened(true)
    if (data.channel?.viewerHasFollowed === undefined && !data.channel?.id)
      return

    if (!data.channel.viewerHasFollowed) {
      const [mutate] = followChannelMutation
      mutate({
        variables: {
          channelId: data.channel.id,
        },
        refetchQueries: [
          {
            query: GqlFollowingsDocument,
            variables: {
              Pagination: {
                page: 1,
                pageSize: 10,
              },
            },
          },
        ],
      })
    }

    if (data.channel.viewerHasFollowed) {
      setUnFollowModalOpened(true)
    }
  }

  const getSize = () => {
    if (!isMobile) {
      return type === 'modal' ? 40 : 50
    }
    if (isMobile) {
      return type === 'feed' || type === 'modal' ? 40 : 35
    }
  }
  return (
    <>
      <StyledWrapper className={className} data-type={type}>
        <div className="lt">
          <Avatar
            type="channel"
            src={data.channel?.owner?.logoImage?.url}
            href={getWebviewHref(`/channel/${data.channel?.url}`, isWebview)}
            size={getSize()}
          />
          <div className="content">
            <Link
              href={getWebviewHref(`/channel/${data.channel?.url}`, isWebview)}
            >
              <a className="channel-name">{data.channel?.name}</a>
            </Link>
            <div className="channel-info">
              {data.isNotice && router.query['channel-url'] && (
                <div className="noticeBadge">공지</div>
              )}
              <div className="date">
                {convertCreatedAtToString(data.createdAt)}
                {data.updatedAt && ' (수정됨)'}
              </div>
            </div>
          </div>
        </div>
        <div className="rt">
          {showFollowBtn && !data.channel?.viewerIsOwner && (
            <Button
              className="rt follow-btn"
              variant={
                data.channel?.viewerHasFollowed ? 'contained' : 'outlined'
              }
              onClick={handleFollowBtn}
              color={data.channel?.viewerHasFollowed ? 'lightGrey2' : 'primary'}
              disabled={data.channel?.viewerHasFollowed}
            >
              {data.channel?.viewerHasFollowed ? '팔로우중' : '+팔로우'}
            </Button>
          )}
          {type !== 'modal' && <DropdownBtn data={data} />}
        </div>
        <LoginAlert
          DialogProps={{
            open: loginModalOpened,
          }}
          onRequestClose={() => setLoginModalOpened(false)}
        />
        <UnFollowModal
          open={unFollowModalOpened}
          channelId={data.channel?.id || ''}
          onClose={() => setUnFollowModalOpened(false)}
        />
      </StyledWrapper>
    </>
  )
}

export default NewsItemHeader

const StyledWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 20px 12px;
  .lt {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 1;
    overflow: hidden;
    .content {
      overflow: hidden;
      .channel-name {
        display: block;
        font-size: 16px;
        font-weight: bold;
        padding-bottom: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .channel-info {
        display: flex;
        align-items: center;
        gap: 4px;
        .noticeBadge {
          color: #479ef8;
          background-color: #fff;
          border: 1px solid #479ef8;
          border-radius: 5px;
          padding: 2px 5px 1px;
          font-weight: 700;
          font-size: 12px;
        }
        .type {
          font-size: 14px;
          padding: 1px 4px;
        }
        .date {
          font-size: 14px;
          color: #9f9f9f;
        }
      }
    }
  }
  .rt {
    flex-grow: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .user-img {
    border-radius: 50%;
    overflow: hidden;
    width: 40px;
    height: 40px;
    margin-right: 12px;
  }
  .more-btn-wrapper {
    position: relative;
    .more-menu-btn {
      .dot {
        background-color: #999999;
      }
    }
    .more-menu-list {
      display: none;
      position: absolute;
      top: calc(100% + 5px);
      right: 0;
      border: 1px solid #cccccc;
      box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.16);
      border-radius: 5px;
      padding: 6px 0;
      text-align: center;
      white-space: nowrap;
      font-size: 12px;
      background-color: white;
      .more-menu {
        display: block;
        padding: 4px 12px;
        &:hover {
          background-color: #f8f8f8;
        }
      }
      &[data-open='true'] {
        display: block;
      }
    }
  }
  &[data-type='modal'] {
    .lt {
      .channel-info {
        .date {
          font-size: 12px;
        }
      }
    }
    .rt {
      flex-shrink: 0;
      align-self: stretch;
    }
  }
  &[data-type='card'] {
    .lt {
      .channel-name {
        font-size: 14px;
      }
      .channel-info {
        .date {
          font-size: 12px;
        }
      }
    }
    .rt {
      flex-shrink: 0;
      align-self: stretch;
    }
  }
  ${mobileBreakPoint} {
    padding: 20px 20px 5px;
    &[data-type='card'] {
      padding: 15px 15px 5px;
    }
    &[data-type='modal'] {
      padding: 0 20px 5px;
      .lt {
        .channel-name {
          color: white;
        }
      }
      .rt {
        display: none;
      }
    }
  }
`
